import React, { useEffect, useState } from 'react'

import ProductsTable from './ProductsTable'

import { PageHeader } from '../../components/typography/PageHeader'
import { useGetAllProductsQuery } from '../../redux-toolkits/products/products.slice'

import { skipToken } from '@reduxjs/toolkit/query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { BulkUploadProductsModal } from '../../components/modal/app_modals/bulkUploadProductsModal'
import { ChooseProductTypeModal } from '../../components/modal/app_modals/chooseProductTypeModal'
import { FilterProductsModal } from '../../components/modal/app_modals/filterProductsModal'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { getPageFromSkip, getSkipFromPage } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import { usePermissions } from '../../hooks/usePermissions'
import {
  ProductSearchQuery,
  ProductStatus,
} from '../../redux-toolkits/products/products.type'

const ProductsPage: React.FC = () => {
  const [query, setQuery] = useState<ProductSearchQuery>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const navigate = useNavigate()
  const { hasPermissions } = usePermissions()

  const { handleModalClose, handleModalOpen, modal, modalOpen, setModalOpen } =
    useModalControl()

  const {
    data: products,
    isFetching: loadingProducts,
    refetch,
  } = useGetAllProductsQuery(
    query
      ? {
          ...query,
          fulfillmentCenterId:
            selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      skip: selectedFulfilmentCenters.length === 0,
    },
  )

  useEffect(() => {
    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 20,
      search: searchParams.get('search') || undefined,
      vendorId: searchParams.get('vendorId') || undefined,
      categoryId: searchParams.get('categoryId') || undefined,
      subcategoryId: searchParams.get('subcategoryId') || undefined,
      status: (searchParams.get('status') as ProductStatus) || undefined,
    })
  }, [searchParams])

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-center gap-4 flex-wrap">
        <PageHeader
          title="Products"
          className="!mb-0 max-md:!w-full md:flex-1"
        />

        {hasPermissions(['view_product', 'update_product']) ? (
          <>
            <Button
              size="medium"
              label="Add New Product"
              primary
              onClick={() => {
                handleModalOpen('chooseProductTypeModal')
              }}
              className="w-fit"
            />
            <Dropdown
              className=""
              menuButton={
                <button type="button">
                  <Icon width={58} height={58} id="verticalDots" />
                </button>
              }
              menuItems={[
                {
                  name: 'Bulk Upload',
                  value: 'edit',
                  onClick: () => {
                    handleModalOpen('bulkUploadProductsModal')
                  },
                },
                {
                  name: 'Bulk Edit Prices',
                  value: 'disable',
                  onClick: () => {
                    navigate('/products/bulk-price-edit')
                  },
                },
              ]}
            />
          </>
        ) : undefined}
      </div>

      <CountCardContainer className="!my-0">
        <CountCard
          isCurrency={false}
          text="Total products count"
          count={products?.data.totalItems ?? 0}
        />
      </CountCardContainer>

      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                search: search,
                skip: '0',
              })
            }}
            placeholder="Search products or creators"
            initialValue={searchParams.get('search') || undefined}
          />
        </div>

        <Button
          label={`Filters`}
          primary={false}
          icon="filter"
          className="!font-normal"
          onClick={() => {
            handleModalOpen('filterModal')
          }}
        />
      </div>

      <ProductsTable
        productList={products?.data.data || []}
        fulfilmentCentres={selectedFulfilmentCenters}
        currentPage={Number(
          getPageFromSkip(query?.skip ?? 0, query?.limit ?? 20),
        )}
        totalDataCount={products?.data.totalItems ?? 0}
        pageLimit={NUMBER_OF_ITEMS_PER_PAGE}
        onPageChange={(page) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            skip: `${getSkipFromPage(page, query?.limit ?? 20)}`,
          })
        }}
        loading={loadingProducts}
        refetch={() => {
          refetch()
        }}
      />

      <BulkUploadProductsModal
        open={modalOpen && modal === 'bulkUploadProductsModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
      />

      <ChooseProductTypeModal
        open={modalOpen && modal === 'chooseProductTypeModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(type) => {
          navigate(`/products/new/${type}`)
        }}
      />

      <FilterProductsModal
        modalOpen={modalOpen && modal === 'filterModal'}
        handleModalClose={() => {
          handleModalClose()
        }}
        setModalOpen={setModalOpen}
        query={query}
        onSucess={(q) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            page: String(q.page ?? 1),
            vendorId: q.vendorId || '',
            categoryId: q.categoryId || '',
            subcategoryId: q.subcategoryId || '',
            status: q.status || '',
          })
        }}
      />
    </div>
  )
}

export default ProductsPage
